import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  REVIEW_TYPE_OF_PROVIDER,
} from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isPickableService,
  isPickableWork,
  pickRelated,
  readRelatedIds,
} from '../../util/relatedListings';
import {
  PRICING_MODEL_HOURLY,
  PRICING_MODEL_MONTHLY,
  isShowableFaqItem,
  isShowablePackage,
} from '../../util/listingPackages';
import { richTextToMetaDescription } from '../../util/seo';

const PUBLIC_PAGE = { name: 'ListingPage' };

/**
 * Where an owner's variant URL (draft or pending approval) sends the viewer
 * once the listing no longer matches it, or null to stay.
 * listingState is null while the own listing is loading; errorStatus is the
 * status of a failed own-listing fetch.
 */
export const ownVariantRedirect = ({ variant, listingState, errorStatus = null }) => {
  const isPendingApprovalVariant = variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = variant === LISTING_PAGE_DRAFT_VARIANT;
  if (!isPendingApprovalVariant && !isDraftVariant) {
    return null;
  }
  // A shared variant URL of someone else's listing: the own-listing fetch is
  // forbidden, so try the public page.
  if (errorStatus === 403) {
    return PUBLIC_PAGE;
  }
  if (!listingState) {
    return null;
  }
  if (isPendingApprovalVariant) {
    return listingState === LISTING_STATE_PENDING_APPROVAL ? null : PUBLIC_PAGE;
  }
  // The draft variant after publishing (e.g. Back from the public page): the
  // draft banner must not offer to publish the listing again.
  if (listingState === LISTING_STATE_DRAFT) {
    return null;
  }
  return listingState === LISTING_STATE_PENDING_APPROVAL
    ? { name: 'ListingPageVariant', variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT }
    : PUBLIC_PAGE;
};

const isRated = review => typeof review.rating === 'number' && Number.isFinite(review.rating);
const roundToTenth = n => Math.round(n * 10) / 10;

/**
 * The owner's banner on their own listing page: which variant to show and
 * where its Edit button leads, or null while the listing state is unknown.
 */
export const ownerBanner = ({ listingState, listingId, slug }) => {
  if (!listingState) {
    return null;
  }
  const isDraft = listingState === LISTING_STATE_DRAFT;
  const variant = isDraft
    ? 'draft'
    : listingState === LISTING_STATE_PENDING_APPROVAL
    ? 'pendingApproval'
    : listingState === LISTING_STATE_CLOSED
    ? 'closed'
    : 'published';
  return {
    variant,
    editParams: {
      id: listingId,
      slug,
      type: isDraft ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT,
      tab: 'description',
    },
  };
};

/**
 * The reviews a service listing shows: the freelancer's on-platform reviews
 * (reviews of the provider, not the ones they wrote of clients) and their
 * off-platform reviews, newest first. `average` (one decimal, or null) and
 * `count` cover the rated reviews, the ones behind "(N reviews)", so the
 * seller bar, SectionReviews and the JSON-LD agree.
 */
export const summarizeReviews = (reviews = [], offPlatformReviews = []) => {
  const onPlatform = reviews
    .filter(review => review.attributes.type === REVIEW_TYPE_OF_PROVIDER)
    .map(review => ({
      content: review.attributes.content,
      rating: review.attributes.rating,
      createdAt: review.attributes.createdAt,
      author: review.author,
      authorName: review.author.attributes.profile.displayName,
      abbreviatedName: review.author.attributes.profile.abbreviatedName,
      type: review.attributes.type,
      id: review.id.uuid,
      onPlatform: true,
    }));

  const offPlatform = offPlatformReviews.map(offReview => ({
    content: offReview.content,
    rating: offReview.reviewRating,
    createdAt: new Date(offReview.createdAt),
    authorName: offReview.displayName,
    abbreviatedName: offReview.abbreviatedName,
    type: REVIEW_TYPE_OF_PROVIDER,
    id: offReview._id,
    onPlatform: false,
  }));

  const combined = [...onPlatform, ...offPlatform].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  const ratings = combined.filter(isRated).map(review => review.rating);
  const count = ratings.length;
  const average = count > 0 ? roundToTenth(ratings.reduce((a, b) => a + b, 0) / count) : null;

  return { reviews: combined, average, count };
};

const isoDate = value => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * The JSON-LD `review` list and `aggregateRating` from summarizeReviews().
 * Without a rated review there is no aggregateRating, so it is never NaN.
 */
export const reviewSchemaFields = ({ reviews, average, count }) => ({
  review: reviews.map(review => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.authorName,
    },
    datePublished: isoDate(review.createdAt),
    reviewBody: review.content,
    ...(isRated(review)
      ? {
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
            bestRating: '5',
            worstRating: '1',
          },
        }
      : {}),
  })),
  ...(count > 0 && Number.isFinite(average)
    ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: average,
          reviewCount: count,
          bestRating: '5',
          worstRating: '1',
        },
      }
    : {}),
});

/**
 * The freelancer as the JSON-LD seller: the ProfilePage's Person node (its
 * @id is `${profileUrl}#person` in buildProfileSchema), so search engines
 * join the service to the profile.
 */
export const sellerSchema = ({ name, profileUrl }) => ({
  '@type': 'Person',
  ...(profileUrl ? { '@id': `${profileUrl}#person` } : {}),
  name,
  ...(profileUrl ? { url: profileUrl } : {}),
});

// UN/CEFACT unit codes, which schema.org expects in unitCode.
const PRICE_UNITS = {
  [PRICING_MODEL_HOURLY]: { unitCode: 'HUR', unitText: 'hour' },
  [PRICING_MODEL_MONTHLY]: { unitCode: 'MON', unitText: 'month' },
};

/**
 * The JSON-LD `offers`: one Offer per package row on the page, named like
 * the row (an old nameless package 1 shows the listing title). The page
 * shows starting prices ("price from"), so each price is also the minPrice
 * of its price specification. Hourly and monthly prices state their unit; a
 * project price has none. Google reads offers.price and ignores the
 * specification, so both carry the price.
 */
export const packageOffersSchema = ({ packages, listingTitle, currency, url, isClosed, seller }) =>
  packages.filter(isShowablePackage).map(pkg => {
    const price = pkg.price / 100;
    const unit = PRICE_UNITS[pkg.pricingModel];
    const name =
      typeof pkg.title === 'string' && pkg.title.trim() ? pkg.title.trim() : listingTitle;
    return {
      '@type': 'Offer',
      name,
      price,
      priceCurrency: currency,
      priceSpecification: {
        '@type': unit ? 'UnitPriceSpecification' : 'PriceSpecification',
        price,
        minPrice: price,
        priceCurrency: currency,
        ...unit,
      },
      url,
      availability: isClosed ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      seller,
    };
  });

/**
 * The FAQPage node for the listing's FAQ: the questions SectionFaq shows
 * that have an answer, in page order, with the answers' plain text. Null
 * without one.
 */
export const faqSchema = ({ faq, url }) => {
  const mainEntity = faq
    .filter(isShowableFaqItem)
    .map(item => ({ name: item.question.trim(), text: richTextToMetaDescription(item.answer) }))
    .filter(item => item.text)
    .map(({ name, text }) => ({
      '@type': 'Question',
      name,
      acceptedAnswer: { '@type': 'Answer', text },
    }));
  return mainEntity.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        about: { '@id': url },
        mainEntity,
      }
    : null;
};

/**
 * "Related work" and "More services" from the author's other listings
 * (newest first): the works and services the freelancer picked for this
 * listing, in pick order and only those, or the newest three of each when
 * none of the picks is visible. serviceCount counts every visible other
 * service for the "All N services" link.
 */
export const relatedListingsForPage = ({ listing, authorListings }) => {
  const publicData = listing?.attributes?.publicData || {};
  const listingId = listing?.id?.uuid || null;
  const listings = authorListings || [];
  const servicePool = listings.filter(l => isPickableService(l, listingId));
  return {
    works: pickRelated(readRelatedIds(publicData.relatedWorkIds), listings.filter(isPickableWork)),
    services: pickRelated(readRelatedIds(publicData.relatedServiceIds), servicePool),
    serviceCount: servicePool.length,
  };
};
